import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Col, Drawer, Row, Space, Tag, Modal } from "antd";
import {
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { BiFilterAlt } from "react-icons/bi";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "antd/lib/form/Form";
import DataTableComponent from "../../shared/components/data-table/data-table.component";
import GenericFormComponent from "../../shared/components/generic-form/generic-form.component";
import ActivityLogComponent from "../../shared/components/activity-log/activity-log.component";
import { ActiveFilterChipsBar } from "../../shared/components/active-filter-chips/active-filter-chips.component";
import { isFilterValueActive } from "../../shared/utils/active-filters";
import { TABLE_ROWS_COUNT } from "../../shared/constants";
import { allPermissions } from "../../shared/constants/constants";
import { RootState } from "../../shared/types/common";
import { accessAllowed, showNotification } from "../../shared/utils/utils";
import {
  deleteRole,
  getRolesList,
} from "../../core/store/reducers/role.slice";
import CreateEditRoleComponent from "./components/create-edit-role.component";

const ROLE_FILTER_LABELS: Record<string, string> = {
  search: "Search",
  status: "Status",
};

function roleFilterValueText(key: string, value: any): string {
  switch (key) {
    case "status":
      return value === true ? "ACTIVE" : "INACTIVE";
    default:
      return String(value).trim();
  }
}

const ROLE_FILTER_CLEAR: Record<string, any> = {
  search: undefined,
  status: undefined,
};

const ManageRolesPage: React.FC = () => {
  const dispatch = useDispatch();
  const [form] = useForm();
  const { loading, list, pagination } = useSelector(
    (state: RootState) => state.role
  );
  const { myPermissions } = useSelector((state: RootState) => state.auth);
  const { screenType } = useSelector((state: RootState) => state.settings);
  const [createEditOpen, setCreateEditOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null as any);
  const [filterVisible, setFilterVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [roleIdToDelete, setRoleIdToDelete] = useState("");
  const [openLogs, setOpenLogs] = useState(false);
  const [filters, setFilters] = useState({} as any);
  const [roleListData, setRoleListData] = useState([] as any);
  const [pageSize, setPageSize] = useState(TABLE_ROWS_COUNT);

  useEffect(() => {
    loadList(1);
  }, [filters, pageSize]);

  useEffect(() => {
    setRoleListData(
      list.map((obj: any, index: number) => ({ key: index, ...obj }))
    );
  }, [list]);

  const loadList = (page: any = null) => {
    const params = {
      page: page ? page : pagination?.page ? pagination?.page : 1,
      limit: pageSize,
    };
    const body = {} as any;
    Object.keys(filters)?.map((key: any) => {
      if (isFilterValueActive(filters[key])) body[key] = filters[key];
    });
    dispatch(getRolesList({ params, body }));
  };

  const createEntityListner = () => {
    setCreateEditOpen(true);
    setSelectedEntity(null);
  };

  const editEntityListner = (entity: any) => {
    setCreateEditOpen(true);
    setSelectedEntity(entity);
  };

  const handleDeleteModal = (roleId: string) => {
    setDeleteModalVisible(true);
    setRoleIdToDelete(roleId);
  };

  const handleDelete = (roleId: string) => {
  (dispatch(deleteRole(roleId)) as any).then((res: any) => {
      if (res.meta.requestStatus === "fulfilled") {
      showNotification("Role Deleted Successfully", "success");
      loadList();
      setDeleteModalVisible(false);
    }
  });

};

  const handleOpenLogs = (record: any) => {
    setOpenLogs(true);
    setSelectedEntity(record);
  };

  const onDeleteConfirm = () => {
    handleDelete(roleIdToDelete);
  };

  const onDeleteCancel = () => {
    setDeleteModalVisible(false);
    setRoleIdToDelete("");
  };

  const createEditSubmit = () => {
    setCreateEditOpen(false);
    loadList();
  };

  const onPageChange = (pageNumber: number) => {
    loadList(pageNumber);
  };
  const onPageSizeChange = (_pageNumber: number, selectedPageSize: number) => {
    setPageSize(selectedPageSize);
  };

  const onFilterApply = (values: any) => {
    setFilters(values);
  };

  const clearAllFilters = useCallback(() => {
    setFilters({});
    form.resetFields();
  }, [form]);

  const removeFilter = useCallback(
    (key: string) => {
      setFilters((prev: any) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      form.setFieldsValue({ [key]: ROLE_FILTER_CLEAR[key] });
    },
    [form]
  );

  const activeFilterEntries = useMemo(() => {
    return Object.keys(filters)
      .filter((key) => isFilterValueActive(filters[key]))
      .map((key) => ({
        key,
        fieldLabel: ROLE_FILTER_LABELS[key] || key,
        valueText: roleFilterValueText(key, filters[key]),
      }));
  }, [filters]);

  const hasAnyRoleAccess =
    accessAllowed(myPermissions, allPermissions.UPDATE_ROLE) ||
    accessAllowed(myPermissions, allPermissions.DELETE_ROLE) ||
    accessAllowed(myPermissions, allPermissions.ROLE_ACTIVITY_LOGS);

  const columns = [
    {
      title: "Role Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Role",
      dataIndex: "code",
      key: "code",
    },
    // {
    //   title: "Users Count",
    //   dataIndex: "usersCount",
    //   key: "usersCount",
    // },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: boolean) => (
        <Tag color={status ? "green" : "default"}>{status ? "ACTIVE" : "INACTIVE"}</Tag>
      ),
    },
    ...(hasAnyRoleAccess
      ? [
          {
            title: "Actions",
            key: "actions",
            fixed: "right",
            render: (_text: any, record: any) => (
              <Space>
                {accessAllowed(myPermissions, allPermissions.UPDATE_ROLE) && (
                  <Button
                    type="link"
                    onClick={() => editEntityListner(record)}
                    icon={<EditOutlined />}
                  />
                )}
                {accessAllowed(myPermissions, allPermissions.DELETE_ROLE) && (
                  <Button
                    type="link"
                    onClick={() => handleDeleteModal(record?.id)}
                    icon={<DeleteOutlined style={{ color: "#FF0000" }} />}
                  />
                )}
                {accessAllowed(myPermissions, allPermissions.ROLE_ACTIVITY_LOGS) && (
                  <Button
                    type="link"
                    onClick={() => handleOpenLogs(record)}
                    icon={<EyeOutlined />}
                  />
                )}
              </Space>
            ),
          },
        ]
      : []),
  ];

  const formProps = {
    form,
    name: "manage-roles-filters",
    enableEnterSubmit: true,
    formElements: [
      {
        name: "search",
        label: "",
        placeholder: "Role Name or Description Search",
        type: "Input",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
      },
      {
        name: "status",
        label: "",
        placeholder: "Status",
        type: "Select",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
        allowClear: true,
        options: [
          { value: true, label: "ACTIVE" },
          { value: false, label: "INACTIVE" },
        ],
      },
    ],
    initialValues: {},
    extraButtons: [
      {
        htmlType: "submit",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 3 },
        loading: false,
        submitButtonText: "Clear",
        type: "clearForm",
        onClick: clearAllFilters,
      },
      {
        htmlType: "submit",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 3 },
        loading: false,
        submitButtonText: "Apply",
        submitButtonType: "primary",
      },
    ],
    onFinish: onFilterApply,
    onFinishFailed: null,
    onValuesChange: () => {
      return;
    },
  };

  const headerButtons = (
    <Space size="middle" className="app-page-actions">
      {accessAllowed(myPermissions, allPermissions.CREATE_ROLE) && (
        <Button type="primary" icon={<PlusOutlined />} onClick={createEntityListner}>
          <span className="font-size-14">New {!screenType?.isPortrait && "Role"}</span>
        </Button>
      )}
      <Button
        shape="circle"
        type="primary"
        icon={<BiFilterAlt />}
        onClick={() => setFilterVisible(!filterVisible)}
        title={filterVisible ? "Hide filters" : "Show filters"}
      />
    </Space>
  );

  return (
    <div className="app-page">
      <div className="app-page-header">
        <Row align="middle" justify="space-between" wrap>
          <Col flex="1" style={{ minWidth: 0 }}>
            <div className="app-page-title-wrap">
               <div className="app-page-icon">
                <UserOutlined />
              </div>
              <h1 className="app-page-title">Manage Roles</h1>
            </div>
            <p className="app-page-subtitle">View and manage roles</p>
          </Col>
          <Col>{headerButtons}</Col>
        </Row>
      </div>

      {filterVisible && (
        <div className="app-filter-bar">
          <div className="app-filter-header">
            <div className="app-filter-title">
              <BiFilterAlt style={{ fontSize: 14 }} />
              Filters
            </div>
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={() => setFilterVisible(false)}
              title="Close filters"
              aria-label="Close filters"
            />
          </div>
          <div className="table-filter-form">
            <GenericFormComponent {...formProps} />
          </div>
        </div>
      )}

      <ActiveFilterChipsBar
        show={!filterVisible}
        entries={activeFilterEntries}
        onRemove={removeFilter}
        onClearAll={clearAllFilters}
      />

      <div className="app-table-card">
        <DataTableComponent
          rowKey="id"
          pagination={true}
          loading={loading}
          dataSource={roleListData}
          columns={columns}
          defaultPage={0}
          currentPage={pagination?.page || 1}
          totalRows={pagination?.total || 0}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          pageSize={pageSize}
        />
      </div>

      <Modal
        title="Confirm Delete"
        open={deleteModalVisible}
        onOk={onDeleteConfirm}
        onCancel={onDeleteCancel}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
      >
        <p>Are you sure you want to delete this role?</p>
      </Modal>

      {createEditOpen && (
        <CreateEditRoleComponent
          onClose={() => setCreateEditOpen(false)}
          visible={createEditOpen}
          selectedEntity={selectedEntity}
          onSubmit={createEditSubmit}
        />
      )}

      {openLogs && (
        <Drawer open={openLogs} onClose={() => setOpenLogs(false)} title="Log Details">
          <ActivityLogComponent
            id={selectedEntity?.id}
            record={selectedEntity}
            module="Role"
          />
        </Drawer>
      )}
    
    </div>
  );

  
};

export default ManageRolesPage;